/**
 * Protected Route Component
 * Phase 6: Frontend Application
 *
 * Wraps routes that require authentication.
 * Redirects to login if user is not authenticated.
 * 
 * Note: Uses accounts check as primary auth signal since useIsAuthenticated
 * can briefly return false during client-side navigation while MSAL
 * re-evaluates state from sessionStorage.
 */

'use client';

import { useAuth, useHasAnyRole, type Role } from '@/lib/auth';
import { logger } from '@/lib/logger';
import { useMsal } from '@azure/msal-react';
import { useEffect, useState, type ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Roles that can access this route. If empty, any authenticated user can access. */
  allowedRoles?: Role[];
  /** Component to show while checking auth */
  loadingComponent?: ReactNode;
  /** Component to show when unauthorised (authenticated but wrong role) */
  unauthorisedComponent?: ReactNode;
}

/**
 * Default loading component
 */
function DefaultLoading(): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent mx-auto" />
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Default unauthorised component
 */
function DefaultUnauthorised(): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
        <p className="text-gray-600 mb-4">
          You do not have permission to access this page.
        </p>
        <a
          href="/"
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          Return to Home
        </a>
      </div>
    </div>
  );
}

/**
 * Protected Route wrapper
 * 
 * Usage:
 * ```tsx
 * <ProtectedRoute allowedRoles={['admin', 'trusted_editor']}>
 *   <AdminPanel />
 * </ProtectedRoute>
 * ```
 */
export function ProtectedRoute({
  children,
  allowedRoles = [],
  loadingComponent,
  unauthorisedComponent,
}: ProtectedRouteProps): JSX.Element {
  const { isAuthenticated, isLoading, login } = useAuth();
  const { accounts } = useMsal();
  const hasRequiredRole = useHasAnyRole(allowedRoles);
  
  // Track if component has mounted and had time to check auth
  // This prevents immediate redirect during client-side navigation
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  
  // Use accounts as a more reliable signal than isAuthenticated
  // accounts array is populated from localStorage synchronously
  // while isAuthenticated may briefly be false during re-renders
  const hasAccounts = accounts.length > 0;
  const isEffectivelyAuthenticated = isAuthenticated || hasAccounts;

  // Give MSAL more time to fully initialise from localStorage on fresh page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setHasCheckedAuth(true);
    }, 500); // Increased to 500ms for full page navigation
    return () => clearTimeout(timer);
  }, []);

  // Redirect to login only if:
  // 1. We've waited for the auth check
  // 2. MSAL is not loading
  // 3. User is not authenticated AND has no accounts
  useEffect(() => {
    if (hasCheckedAuth && !isLoading && !isEffectivelyAuthenticated) {
      logger.debug('ProtectedRoute: Redirecting to login - no accounts found');
      login().catch((e) => logger.error('Login redirect failed', e));
    }
  }, [hasCheckedAuth, isLoading, isEffectivelyAuthenticated, login]);

  // Show loading while checking auth state or waiting for initial check
  if (isLoading || !hasCheckedAuth) {
    return <>{loadingComponent || <DefaultLoading />}</>;
  }

  // Not authenticated - show loading state while redirecting to login
  // Do NOT render children in this state to prevent data fetch errors
  if (!isEffectivelyAuthenticated) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent mx-auto" />
          <p className="text-gray-600">Redirecting to sign in...</p>
        </div>
      </div>
    );
  }

  // Check role-based access (if roles specified)
  if (allowedRoles.length > 0 && !hasRequiredRole) {
    return <>{unauthorisedComponent || <DefaultUnauthorised />}</>;
  }

  // Authenticated and authorised
  return <>{children}</>;
}
